import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import {
  classifyErrorBadge,
  ERROR_DEFINITIONS,
} from "@/features/acquireBookStatus/utils/errorClassifier";
import { type AcquiredDocument, type AcquiredPropertyFilters } from "../types";
import { OPTION_LABELS, formatClosedDate } from "../utils/documentPresentation";

export const isDateInSearchRange = (
  document: AcquiredDocument,
  filters: AcquiredPropertyFilters
): boolean =>
  document.status === "SUCCEEDED" &&
  document.acquiredDate !== null &&
  Boolean(filters.acquiredDateFrom || filters.acquiredDateTo) &&
  (!filters.acquiredDateFrom ||
    document.acquiredDate >= filters.acquiredDateFrom) &&
  (!filters.acquiredDateTo || document.acquiredDate <= filters.acquiredDateTo);

export const DocumentCell: React.FC<{
  documents: AcquiredDocument[];
  filters: AcquiredPropertyFilters;
}> = ({ documents, filters }) => {
  if (documents.length === 0) return <>—</>;
  return (
    <Stack
      spacing={0.5}
      sx={{
        width: "100%",
        py: 0.5,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        textAlign: "center",
      }}
    >
      {documents.map((document) => {
        const error =
          document.status === "FAILED"
            ? classifyErrorBadge(document.errorDetail, document.errorCode) ??
              ERROR_DEFINITIONS.GENERIC_FAILED
            : null;
        const errorDescription = [
          error?.label,
          document.errorCode && `エラーコード: ${document.errorCode}`,
          document.errorDetail,
        ]
          .filter(Boolean)
          .join("\n");
        return (
          <Box
            key={document.id}
            sx={{ "& + &": { borderTop: 1, borderColor: "divider", pt: 0.5 } }}
          >
            {error ? (
              <Tooltip title={errorDescription} placement="top" arrow>
                <Box
                  component="span"
                  tabIndex={0}
                  sx={{
                    display: "inline-block",
                    px: 1,
                    py: 0.25,
                    fontSize: "0.75rem",
                    borderRadius: "999px",
                    border: `2px solid ${error.color.text}`,
                    color: error.color.text,
                    fontWeight: 600,
                    minWidth: 72,
                    textAlign: "center",
                  }}
                >
                  {error.label}
                </Box>
              </Tooltip>
            ) : document.status === "SUCCEEDED" &&
              document.uri &&
              /^https?:\/\//i.test(document.uri) ? (
              <Tooltip title="ダウンロードできます">
                <IconButton
                  href={document.uri}
                  aria-label={`書類 ${document.id} をダウンロード`}
                >
                  <CloudDownloadOutlinedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Typography variant="body2" component="span">
                {document.status === "SUCCEEDED" ? "成功" : "処理中"}
              </Typography>
            )}
            <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
              {document.status === "SUCCEEDED"
                ? document.acquiredDate
                  ? document.acquiredDate.replace(/-/g, "/")
                  : "取得日不明"
                : "—"}
            </Typography>
            {isDateInSearchRange(document, filters) && (
              <Typography variant="caption" color="info.main" display="block">
                期間内
              </Typography>
            )}
            {document.options?.map((option) => (
              <Typography key={option} variant="caption" display="block">
                {OPTION_LABELS[option] ?? option}
              </Typography>
            ))}
            {document.options?.includes("HEISATOUKIBO") &&
              document.pictureUpdateTime && (
                <Typography variant="caption" display="block">
                  閉鎖年月日: {formatClosedDate(document.pictureUpdateTime)}
                </Typography>
              )}
          </Box>
        );
      })}
    </Stack>
  );
};
